import { useEffect, useState } from "react";
import { toast } from "sonner";
import { enablePush, getCurrentSubscription } from "@/lib/push";

/**
 * Non-intrusive purple nudge shown on the Dashboard when the user hasn't
 * enabled Web Push yet. Dismissible per-session (localStorage flag).
 * Auto-hides once the user opts in.
 */
export const PushPermissionNudge = () => {
  const [status, setStatus] = useState("checking"); // checking | needed | enabled | dismissed | busy
  const dismissedKey = "mh-push-nudge-dismissed";

  useEffect(() => {
    (async () => {
      // Browser doesn't support push at all → hide the nudge silently
      if (typeof window === "undefined" || !("serviceWorker" in navigator) || !("PushManager" in window)) {
        setStatus("enabled");
        return;
      }
      // Already permission-denied → don't nag
      if (Notification && Notification.permission === "denied") {
        setStatus("enabled");
        return;
      }
      if (localStorage.getItem(dismissedKey) === "1") {
        setStatus("dismissed");
        return;
      }
      try {
        const sub = await getCurrentSubscription();
        setStatus(sub ? "enabled" : "needed");
      } catch (_e) {
        setStatus("needed");
      }
    })();
  }, []);

  if (status !== "needed") return null;

  const turnOn = async () => {
    setStatus("busy");
    try {
      await enablePush();
      toast.success("Push notifications enabled — you'll get badge unlocks + alerts");
      setStatus("enabled");
    } catch (err) {
      toast.error(err?.message || "Could not enable push — check your browser settings");
      setStatus("needed");
    }
  };

  const dismiss = () => {
    localStorage.setItem(dismissedKey, "1");
    setStatus("dismissed");
  };

  return (
    <div
      data-testid="push-nudge-banner"
      className="mb-6 flex flex-wrap items-center justify-between gap-3 border border-[#a855f7]/40 bg-[#a855f7]/10 px-4 py-3"
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-2xl">🔔</span>
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#a855f7] font-mono font-bold">Turn on push</div>
          <div className="text-sm text-white leading-tight">Get instant alerts when you unlock a new badge or when payments-mode goes live.</div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          data-testid="push-nudge-enable"
          onClick={turnOn}
          className="text-[10px] tracking-[0.3em] uppercase text-black bg-[#a855f7] hover:bg-[#c084fc] px-4 py-2 font-black transition-colors"
        >
          Enable
        </button>
        <button
          data-testid="push-nudge-dismiss"
          onClick={dismiss}
          className="text-[10px] tracking-[0.3em] uppercase text-zinc-300 hover:text-white px-3 py-2 font-mono transition-colors"
        >
          Not now
        </button>
      </div>
    </div>
  );
};
